import React, { useMemo } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowRightIcon, CheckIcon, PhoneIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useSeo } from '../hooks/useSeo';
import { getService, services } from '../data/services';
import { projects } from '../data/projects';
import { cities } from '../data/cities';
import { company } from '../data/company';
import { PageHero } from '../components/PageHero';
import { BeforeAfterSlider } from '../components/BeforeAfterSlider';
import { CtaBand } from '../components/sections/CtaBand';
import { ContactSection } from '../components/sections/ContactSection';
import { breadcrumbSchema, serviceSchema } from '../utils/schema';
export function ServiceDetail() {
  const {
    slug
  } = useParams();
  const {
    t,
    b,
    lang
  } = useLanguage();
  const service = getService(slug);
  const related = useMemo(() => projects.filter(project => project.serviceSlug === slug), [slug]);
  const schema = useMemo(() => {
    if (!service) return [];
    return [serviceSchema(service.title.en, service.short.en), breadcrumbSchema([{
      name: 'Home',
      path: '/'
    }, {
      name: 'Services',
      path: '/#services'
    }, {
      name: service.title.en,
      path: `/services/${service.slug}`
    }])];
  }, [service]);
  useSeo({
    title: service ? `${b(service.title)} in Washington | ${company.shortName}` : `${company.shortName} | Washington`,
    description: service ? b(service.short) : '',
    keywords: service?.keywords,
    schema
  });
  if (!service) return <Navigate to="/" replace data-id="element-494" data-mp="fsud5hawwag5" />;
  const others = services.filter(item => item.slug !== service.slug).slice(0, 4);
  return <>
      <PageHero eyebrow={lang === 'es' ? 'Servicio' : 'Service'} title={`${b(service.title)} — ${lang === 'es' ? 'Washington' : 'Washington State'}`} description={b(service.short)} image={service.image} imageAlt={b(service.imageAlt)} crumbs={[{
      name: t('nav.home'),
      path: '/'
    }, {
      name: t('nav.services'),
      path: '/#services'
    }, {
      name: b(service.title)
    }]} data-id="element-495" data-mp="krylk5q0i45f" />

      <section className="bg-white py-16 md:py-20" data-id="element-496" data-mp="23plguy9po87v">
        <div className="mx-auto grid max-w-page gap-12 px-4 sm:px-6 lg:grid-cols-[1.2fr_1fr] lg:gap-16 lg:px-8" data-id="element-497" data-mp="23qd15cxuyjiu">
          <div data-id="element-498" data-mp="1zmot6t5bdlx2">
            <h2 className="font-display text-2xl font-bold text-navy md:text-3xl" data-id="element-499" data-mp="2weshq3wn19wa">{t('services.whatIncluded')}</h2>
            <ul className="mt-7 space-y-4" data-id="element-500" data-mp="2rf63ps8zjpmf">
              {service.bullets.map(bullet => <li key={bullet.en} className="flex items-start gap-3 text-base text-ink-soft" data-id="element-501" data-mp="2ndksscg0hlev">
                  <CheckIcon className="mt-0.5 h-5 w-5 shrink-0 text-brand-700" aria-hidden="true" data-id="element-502" data-mp="2ifiyswh1s1om" />
                  {b(bullet)}
                </li>)}
            </ul>

            <img src={service.image} alt={b(service.imageAlt)} loading="lazy" className="mt-10 aspect-[3/2] w-full rounded-lg object-cover" data-id="element-503" data-mp="16zude9jfzapx" />

            <h3 className="mt-12 font-display text-xl font-bold text-navy" data-id="element-504" data-mp="3nm0yqz4evzmj">
              {lang === 'es' ? 'Ciudades donde ofrecemos este servicio' : 'Cities where we provide this service'}
            </h3>
            <ul className="mt-5 flex flex-wrap gap-2" data-id="element-505" data-mp="9qae313nrno">
              {cities.map(city => <li key={city.slug} data-id="element-506" data-mp="58t2kmvze3e8">
                  <Link to={`/service-areas/${city.slug}`} className="inline-flex rounded-full border border-navy/15 px-3.5 py-1.5 text-sm font-semibold text-ink-soft transition-colors duration-150 ease-smooth hover:border-brand-700 hover:text-brand-700" data-id="element-507" data-mp="57rrwjivnix1">
                    {b(service.title)} — {city.name}, WA
                  </Link>
                </li>)}
            </ul>
          </div>

          <aside className="lg:sticky lg:top-28 lg:self-start" data-id="element-508" data-mp="2kyw46qguw8y4">
            <div className="rounded-lg border border-navy/10 bg-mist p-6" data-id="element-509" data-mp="2l18v3t4ldtnb">
              <h2 className="font-display text-lg font-bold text-navy" data-id="element-510" data-mp="1pnftgbr1zwwg">{t('cta.scheduleToday')}</h2>
              <p className="mt-3 text-sm leading-relaxed text-ink-muted" data-id="element-511" data-mp="1lne1wvf18juw">{t('contact.sub')}</p>
              <Link to="/#contact" className="mt-5 inline-flex w-full items-center justify-center rounded-md bg-cta px-5 py-3.5 text-base font-bold text-white transition-colors duration-200 ease-smooth hover:bg-cta-dark" data-id="element-512" data-mp="1zmwziua7wcsy">
                {t('cta.scheduleInspection')}
              </Link>
              <a href={company.phoneHref} className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-md border-2 border-navy px-5 py-3 text-base font-bold text-navy transition-colors duration-200 ease-smooth hover:bg-navy hover:text-white" data-id="element-513" data-mp="1gokot43nquv9">
                <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-514" data-mp="j9gip3crdhlb" />
                {company.phone}
              </a>
            </div>

            <div className="mt-8" data-id="element-515" data-mp="2uvh9a3w5w3vm">
              <h2 className="font-display text-lg font-bold text-navy" data-id="element-516" data-mp="2uufq1c4zsb2t">{t('services.related')}</h2>
              <ul className="mt-4 space-y-2" data-id="element-517" data-mp="2zpbu5ani6owa">
                {others.map(item => <li key={item.slug} data-id="element-518" data-mp="2lxotyzqfww88">
                    <Link to={`/services/${item.slug}`} className="group flex items-center justify-between gap-3 rounded-md border border-navy/10 px-4 py-3 text-sm font-semibold text-ink-soft transition-colors duration-150 ease-smooth hover:border-brand-700 hover:text-brand-700" data-id="element-519" data-mp="2px607vdoxxeg">
                      {b(item.title)}
                      <ArrowRightIcon className="h-4 w-4 shrink-0 transition-transform duration-200 ease-smooth group-hover:translate-x-1" aria-hidden="true" data-id="element-520" data-mp="39leucaysodgl" />
                    </Link>
                  </li>)}
              </ul>
            </div>
          </aside>
        </div>
      </section>

      {related.length > 0 && <section className="bg-mist py-16 md:py-20" data-id="element-521" data-mp="3uwm67w6ib9su">
          <div className="mx-auto max-w-page px-4 sm:px-6 lg:px-8" data-id="element-522" data-mp="3uwvu9e7zr6vw">
            <h2 className="font-display text-2xl font-bold text-navy md:text-3xl" data-id="element-523" data-mp="3q4ug1mr9j638">
              {lang === 'es' ? 'Proyectos relacionados' : 'Related projects'}
            </h2>
            <div className="mt-8 grid gap-6 md:grid-cols-2" data-id="element-524" data-mp="3h4o62aurwo8t">
              {related.map(project => <article key={project.id} className="overflow-hidden rounded-lg border border-navy/10 bg-white" data-id="element-525" data-mp="3c5bg3h8lv120">
                  <BeforeAfterSlider before={project.before} after={project.after} beforeAlt={b(project.beforeAlt)} afterAlt={b(project.afterAlt)} className="aspect-[3/2] w-full" data-id="element-526" data-mp="37600wcnw03mj" />
                  <div className="p-6" data-id="element-527" data-mp="11djnjm67b1za">
                    <p className="text-xs font-bold uppercase tracking-wider text-brand-700" data-id="element-528" data-mp="11bp4yeiejlyd">
                      {b(project.label)} • {project.city} • {project.year}
                    </p>
                    <p className="mt-3 text-sm leading-relaxed text-ink-muted" data-id="element-529" data-mp="2o8oorf2x4153">{b(project.description)}</p>
                  </div>
                </article>)}
            </div>
          </div>
        </section>}

      <CtaBand data-id="element-530" data-mp="20ckatyq9janw" />
      <ContactSection data-id="element-531" data-mp="20d3rlc46ft0c" />
    </>;
}